import { isNil, isNull } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Field } from '@shared/data/entity/fields/Field';
import { useDefaultFields } from '@shared/scenes/entity/create/hooks/useDefaultFields';
import {
  FieldRecord,
  GetUpdatedField,
  RawFieldData,
  UpdateField,
} from '@shared/scenes/entity/types';
import {
  getModifiedFields,
  isFieldModified,
} from '@shared/scenes/entity/util/isFieldRecordModified';
import { API } from '@shared/types/api';

interface EditFieldsHooks {
  fields: FieldRecord;
  originalFields: FieldRecord | null;
  modifiedFields: FieldRecord | null;
  isDirty: boolean;
  updateField: UpdateField;
  resetFields: () => void;
}

interface UseCreateEntityFieldsProps {
  workflowTemplate?: API.WorkflowTemplateFragment;
  viewTemplate?: API.ViewTemplateFragment;
  queryFieldOverrides?: RawFieldData;
  shouldUseDefaults?: boolean;
  countNullAsModified?: boolean;
  /**
   * When true, hold off seeding the form until the source card for a duplicate
   * has loaded. Prevents seeding once with empty overrides and then ignoring
   * the late-arriving values.
   */
  isDuplicateLoading?: boolean;
  /**
   * Whether to wait for the view template before seeding defaults. Card forms decide this with
   * requiresViewTemplate (see useCreateCardEntity); false seeds from the template alone.
   */
  isViewTemplateRequired?: boolean;
}

export function useCreateEntityFields({
  workflowTemplate,
  viewTemplate,
  queryFieldOverrides,
  shouldUseDefaults = true,
  countNullAsModified = false,
  isDuplicateLoading = false,
  isViewTemplateRequired = true,
}: UseCreateEntityFieldsProps): EditFieldsHooks {
  const [originalFields, setOriginalFields] = useState<FieldRecord | null>(null);
  const [fields, setFields] = useState<FieldRecord>({});
  const modifiedFields = useMemo(
    () => getModifiedFields(originalFields, fields, countNullAsModified),
    [originalFields, fields, countNullAsModified],
  );

  const updateField = useCallback(
    (componentId: string, field: Field | null | GetUpdatedField, skipEqualityCheck = false) => {
      setFields(previousFields => {
        const previousField = previousFields?.[componentId];

        let updatedField: Field | null;
        if (typeof field === 'function') {
          updatedField = field(previousField);
        } else {
          updatedField = field;
        }

        if (
          skipEqualityCheck ||
          isFieldModified(previousField, updatedField, countNullAsModified)
        ) {
          return { ...previousFields, [componentId]: updatedField };
        } else {
          return previousFields;
        }
      });
    },
    [countNullAsModified],
  );

  const isDirty = !isNil(modifiedFields) && Object.keys(modifiedFields).length > 0;
  const defaultFields = useDefaultFields({
    workflowTemplateId: workflowTemplate?.id,
    viewTemplate,
    isViewTemplateRequired,
    queryFieldOverrides,
  });

  useEffect(() => {
    if (!isDuplicateLoading && !isNull(defaultFields) && isNull(originalFields)) {
      setOriginalFields(shouldUseDefaults ? defaultFields : {});
      setFields(shouldUseDefaults ? defaultFields : {});
    }
  }, [defaultFields, originalFields, shouldUseDefaults, isDuplicateLoading]);

  const resetFields = useCallback(() => {
    setOriginalFields(defaultFields);
    setFields(defaultFields ?? {});
  }, [defaultFields]);

  return useMemo(() => {
    return {
      fields,
      originalFields,
      modifiedFields,
      isDirty,
      updateField,
      resetFields,
    };
  }, [fields, originalFields, modifiedFields, isDirty, updateField, resetFields]);
}
